import React, { useEffect, useState } from "react";

import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import { useSelector } from "react-redux";
import Logo from "../../../assets/images/svg/Logo.svg";
import { Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

const DemoInvoiceAutomation = () => {
  const loginData = useSelector((state) => state.loginData.value);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [userStatus, setUserStatus] = useState({});
  const [invoiceList, setInvoiceList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: "error" | "info", text: string }
  const [invoicepage, setInvoicePage] = useState(false);

  // Load default file on mount
  useEffect(() => {
    const loadDefaultFile = async () => {
      const candidates = [
        "/samples/vendor-sample.xlsx",
        "/samples/Vendor Sample Data for Invoice gen.xlsx", // original with spaces (URL-encoded by browser)
      ];
      let loaded = false;
      for (const path of candidates) {
        try {
          const res = await fetch(path);
          if (!res.ok) throw new Error("Not found");
          const blob = await res.blob();
          const fileName = path.split("/").pop();
          const file = new File([blob], fileName, {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          setSelectedFile(file);
          setIsDefault(true);
          setMessage({
            type: "info",
            text: "If you wish to use your own Excel file for the POC, you can simply replace the sample file uploaded on the platform",
          });
          loaded = true;
          break;
        } catch (err) {
          // try next candidate
        }
      }
      if (!loaded) {
        setMessage({
          type: "error",
          text: "Failed to load default sample file. Please attach manually in .csv or .xlsx format.",
        });
      }
    };
    loadDefaultFile();
  }, []);

  const resetMessage = () => setMessage(null);

  const handleFileChange = (e) => {
    resetMessage();
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = [".csv", ".xlsx"];
    const nameLower = file.name.toLowerCase();
    const isValid = allowed.some((ext) => nameLower.endsWith(ext));
    if (!isValid) {
      setMessage({
        type: "error",
        text: "Invalid format. Please attach a .csv or .xlsx file similar to the default.",
      });
      return;
    }
    setSelectedFile(file);
    setIsDefault(false);
    setInvoiceList([]);
  };

  const handleRemove = () => {
    setSelectedFile(null);
    setIsDefault(false);
    setInvoiceList([]);
    setMessage({
      type: "info",
      text: "Default file removed. Please attach same file format which we defaultly given.",
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({
        type: "error",
        text: "No file selected. Please choose a .csv or .xlsx file.",
      });
      return;
    }

    // Validate format again before upload
    const allowed = [".csv", ".xlsx"];
    const nameLower = selectedFile.name.toLowerCase();
    const isValid = allowed.some((ext) => nameLower.endsWith(ext));
    if (!isValid) {
      setMessage({
        type: "error",
        text: "Invalid file format. Only .csv or .xlsx allowed.",
      });
      return;
    }

    setLoading(true);
    setMessage(null);
    setInvoiceList([]);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(
        API_ENDPOINT.invoice_generate,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            // optional: could show overall file upload progress here
          },
        }
      );

      const invoices = response.data.invoices || [];

      // Simulate row-wise status updates
      let progressMap = {};
      for (let i = 0; i < invoices.length; i++) {
        const invoice = invoices[i];
        const email = invoice.vendor?.email || `row_${i}`;

        progressMap[email] = `Generating... ${Math.round(
          ((i + 1) / invoices.length) * 100
        )}%`;
        setUserStatus({ ...progressMap });

        await new Promise((resolve) => setTimeout(resolve, 300)); // simulate delay

        progressMap[email] = "✅ Done!";
        setUserStatus({ ...progressMap });
      }

      setInvoiceList(invoices);
    } catch (error) {
      console.error("Upload failed", error);
      setMessage({
        type: "error",
        text: "Invoice generation failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = (invoice) => {
    try {
      const byteChars = atob(invoice.pdf_base64);
      const byteArray = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteArray[i] = byteChars.charCodeAt(i);
      }

      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = invoice.filename || "Invoice.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download error", err);
      setMessage({
        type: "error",
        text: "Failed to download PDF.",
      });
    }
  };

  const switchInvoice = () => {
    setInvoicePage((prev) => !prev);
  };

  return (
    <div style={{ margin: "1rem" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          background: "#ffffff",
          borderRadius: "10px",
          marginBottom: "24px",
          boxShadow: "0 12px 32px rgba(31,45,58,0.08)",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontSize: "28px", fontWeight: 700, margin: 0 }}
          >
            Invoice Automation
          </Typography>
        </div>
        <img
          src={Logo}
          alt="logo"
          width="110"
          style={{ objectFit: "contain", borderRadius: 6 }}
        />
      </div>

      {/* Upload Card */}
      <div
        style={{
          padding: 28,
          fontSize: "16px",
          fontWeight: 500,
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 16px 48px rgba(31,45,58,0.06)",
          maxWidth: 950,
          marginBottom: 30,
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <div style={{ marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: "1.85rem", fontWeight: 700 }}>
            Merchant Invoice Auto-Generation
          </h2>
        </div>

        {/* File upload */}
        <div
          style={{
            background: "#f0f4f8",
            padding: "24px",
            borderRadius: "10px",
            maxWidth: "100%",
            marginBottom: "0",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Tooltip
              title="Upload your Excel file to test the POC with your data"
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#1f2d4a",
                    color: "#ffffffff",
                    fontSize: "0.95rem", 
                    px: 2,
                    py: 1.25,
                    borderRadius: 1,
                    boxShadow: "0 12px 32px rgba(31,45,58,0.2)",
                    maxWidth: 260,
                    lineHeight: 1.4,
                  },
                },
              }}
            >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "12px 20px",
                  backgroundColor: "#3f51b5",
                  color: "#fff",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: 600,
                  transition: "filter .2s",
                  boxShadow: "0 8px 24px rgba(63,81,181,0.2)",
                }}
              >
                <span>Select Excel / CSV</span>

                <input
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
              </label>
            </Tooltip>
            {selectedFile && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  flexGrow: 1,
                }}
              >
                <div
                  style={{
                    background: "#eef4fa",
                    padding: "8px 14px",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "15px",
                    flexWrap: "wrap",
                  }}
                >
                  <strong style={{ marginRight: 6 }}>Selected:</strong>{" "}
                  <span>{selectedFile.name}</span>
                  {isDefault && (
                    <span
                      style={{
                        background: "none",
                        padding: "4px 10px",
                        borderRadius: 999,
                        fontSize: "12px",
                        marginLeft: 6,
                        display: "inline-block",
                        border:"0.5px solid gray"
                      }}
                    >
                      default
                    </span>
                  )}
                </div>
                <button
                  onClick={handleRemove}
                  style={{
                    padding: "8px 16px",
                    background: "#f5f5f7",
                    border: "1px solid #d1d7e0",
                    borderRadius: 8,
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                  title="Remove file"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 4,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={loading}
                style={{
                  padding: "12px 22px",
                  backgroundColor: "#1f4ed8",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: loading ? "not-allowed" : "pointer",
                  fontSize: "16px",
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: "0 12px 32px rgba(31,76,216,0.3)",
                  transition: "background .2s",
                }}
              >
                {loading ? "Generating..." : "Generate Invoices"}
              </button>
            )}
            <div
              style={{
                marginLeft: "auto",
                fontSize: "14px",
                color: "#556179",
                alignSelf: "center",
              }}
            >
              <span>Supported: .xlsx, .csv</span>
            </div>
          </div>

          {message && (
            <div
              style={{
                marginTop: 12,
                padding: "12px 16px",
                borderRadius: 8,
                background: message.type === "error" ? "#ffe6e6" : "#eef7ff",
                color: message.type === "error" ? "#a33" : "#036",
                fontSize: "15px",
                fontWeight: 500,
              }}
            >
              {message.text}
            </div>
          )}
        </div>

        {/* POC Overview */}
        <div
          style={{
            background: "#f5f8fd",
            borderRadius: 10,
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>
            POC-Invoice Automation Overview
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "1.2rem",
              lineHeight: 1.6,
              color: "#1f2d3a",
            }}
          >
            The attached sample file contains details such as Merchant Name,
            Merchant Code, Merchant Email ID, and Settlement Amount, allowing
            users to run the POC and experience the efficiency of our
            automation. Using the data from the Excel file, the platform
            retrieves information from the Merchant Master and Payee databases,
            generates the Merchant’s invoice within 3 seconds, and pushes it to
            the Merchant portal for validation and approval.
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "1.2rem",
              lineHeight: 1.6,
              color: "#1f2d3a",
            }}
          >
            This POC concludes at the invoice generation stage; however, in the
            complete product flow, the process continues through final approval
            and seamlessly pushes the data to the Payee’s Accounts Payable (AP)
            system to complete the merchant payment settlement.
          </p>
          <p
            style={{
              margin: 0,
              fontSize: "1.2rem",
              lineHeight: 1.6,
              color: "#1f2d3a",
            }}
          >
            Approvals can be completed via e-signature or by entering an OTP
            sent to the Merchant’s registered mobile number. Additionally, the
            platform supports API integrations to directly fetch data from the
            Payee’s financial system or ERP, eliminating the need to upload
            Excel files.
          </p>
        </div>
      </div>

      {/* Status */}
      {Object.keys(userStatus).length > 0 && (
        <div style={{ marginTop: 20, fontSize: "16px" }}>
          <strong>Status:</strong>
          <ul style={{ marginTop: 10, paddingLeft: 16 }}>
            {Object.entries(userStatus).map(([email, status], i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <strong>{email}</strong>: {status}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Invoice List */}
      {invoiceList.length > 0 && (
        <div
          style={{
            marginTop: 24,
            overflowX: "auto",
            background: "#fff",
            borderRadius: 10,
            padding: 16,
            boxShadow: "0 16px 48px rgba(31,45,58,0.06)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "16px",
              maxWidth: "100%",
              minWidth: 700,
            }}
          >
            <thead style={{ backgroundColor: "#dde8f3" }}>
              <tr>
                <th
                  style={{
                    padding: 14,
                    border: "1px solid #ccc",
                    textAlign: "left",
                  }}
                >
                  Merchant Code/ Vendor Code
                </th>
                <th
                  style={{
                    padding: 14,
                    border: "1px solid #ccc",
                    textAlign: "left",
                  }}
                >
                  Merchant Name/ Vendor Name
                </th>
                <th
                  style={{
                    padding: 14,
                    border: "1px solid #ccc",
                    textAlign: "left",
                  }}
                >
                  Email
                </th>
                <th
                  style={{
                    padding: 14,
                    border: "1px solid #ccc",
                    textAlign: "right",
                  }}
                >
                  Transaction Amount
                </th>
                <th
                  style={{
                    padding: 14,
                    border: "1px solid #ccc",
                    textAlign: "center",
                  }}
                >
                  Invoice
                </th>
              </tr>
            </thead>
            <tbody>
              {invoiceList.map((invoice, index) => (
                <tr
                  key={index}
                  style={{
                    background: index % 2 === 0 ? "#f7f9fc" : "#ffffff",
                    transition: "background .2s",
                  }}
                >
                  <td
                    style={{
                      padding: 12,
                      border: "1px solid #ccc",
                    }}
                  >
                    {invoice?.invoice_id}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: "1px solid #ccc",
                    }}
                  >
                    {invoice.vendor?.company_name}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: "1px solid #ccc",
                    }}
                  >
                    {invoice.vendor?.email}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: "1px solid #ccc",
                      textAlign: "right",
                    }}
                  >
                    ₹{invoice.vendor?.amount}
                  </td>
                  <td
                    style={{
                      padding: 12,
                      border: "1px solid #ccc",
                      textAlign: "center",
                    }}
                  >
                    <button
                      onClick={() => downloadPDF(invoice)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "20px",
                        cursor: "pointer",
                      }}
                      title="Download PDF"
                    >
                      📄
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DemoInvoiceAutomation;